import React from "react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import shouldForwardProp from "@styled-system/should-forward-prop";
import { ListItem } from "../../";
import { EmotionComponent } from "../../styled";
import {
  color,
  typography,
  system,
  space,
  layout,
  border,
  compose,
  ColorProps,
  TypographyProps,
  SpaceProps,
  LayoutProps,
  BorderProps,
  OverrideNativeElementProps,
} from "../../utils/styleFn";

export const listStyleProps = compose(color, typography, space, layout, border);

export type ListStyleProps = OverrideNativeElementProps<
  ColorProps & TypographyProps & SpaceProps & LayoutProps & BorderProps,
  "ul"
>;

export type ListProps = ListStyleProps & {
  /** ordered list or unordered list */
  ordered?: boolean;
  /** show in how many columns */
  columns?: number;
  /** ListItem exclude the last one's border bottom width and style like "0", "1px solid " */
  itemSeparator?: string | string[];
  /** ListItem exclude the last one's border bottom color */
  itemSeparatorColor?: string | string[];
  /** css list-style-type prop*/
  type?: string;
};

const columnsPropertyTransform = system({
  columns: {
    property: "flexBasis",
    transform: (n) => `${Math.floor(10000 / n) / 100}%`,
  },
});

const separatorPropertyTransform = system({
  itemSeparator: {
    properties: ["borderBottom"],
    scale: "borders",
  },
  itemSeparatorColor: {
    property: "borderBottomColor",
    scale: "colors",
  },
});

const ListBase: EmotionComponent<ListProps> = styled("ul", {
  shouldForwardProp,
})<ListProps>`
  padding-left: 0;
  list-style-position: inside;
  list-style-type: ${(props) => props.type || "none"};
  ${(props) =>
    props.columns &&
    props.columns > 1 &&
    css`
      display: flex;
      flex-wrap: wrap;
      > ${ListItem} {
        display: inline-block;
        ${columnsPropertyTransform(props)};
      }
    `};
  ${(props) =>
    props.itemSeparator &&
    props.itemSeparatorColor &&
    css`
      ${ListItem} {
        &:not(:last-child) {
          ${separatorPropertyTransform(props)};
        }
      }
    `};
  ${listStyleProps}
`;

export const List: React.FC<ListProps> = React.forwardRef((props, ref) => {
  const Comp = props.ordered
    ? (ListBase.withComponent("ol") as EmotionComponent<ListProps>)
    : ListBase;
  return <Comp ref={ref} {...props} />;
});

List.displayName = "List";

List.defaultProps = {
  ordered: false,
  columns: 1,
  itemSeparator: "none",
  itemSeparatorColor: "ui.borderColor",
};

export default List;
